import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["appHub", "children", "withoutSidebar", "onReady", "onContentReady", "onContentError", "iframeContainerID"],
  _excluded2 = ["handleMessage", "show"],
  _excluded3 = ["handleMessage", "show"],
  _excluded4 = ["handleMessage", "show"],
  _excluded5 = ["show"];
import { c as _c } from "react-compiler-runtime";
import I18n from 'I18n';
import { useEffect, useMemo, useRef, useState } from 'react';
import CopilotProvider from 'copilot-toolkit/CopilotProvider';
import { IS_COMMENT_MODE_ACTIVE } from '../constants/CollaborationSidebarStateProperties';
import CollaborationSidebarStateContext from '../contexts/CollaborationSidebarStateContext';
import UserSettingsProvider from '../contexts/UserSettingsProvider';
import usePersistPropsToCollaborationSidebarState from '../hooks/usePersistPropsToCollaborationSidebarState';
import { showWarningNotification } from '../utils/notifications';
import { getIsCommentingUrl } from '../utils/urls';
import CommentModeIndicator from 'collaboration-sidebar/features/deepCommenting/components/CommentModeIndicator';
import CollaborationSidebarPropsContext from '../contexts/CollaborationSidebarPropsContext';
import useEmbeddedComments from '../features/deepCommenting/hooks/useEmbeddedComments';
import useHasUnreadComments from '../hooks/useHasUnreadComments';
import useSidebarIframeHandlers from '../hooks/useSidebarIframeHandlers';
import useConfirmDeleteCommentModal from 'collaboration-sidebar/hooks/modals/useConfirmDeleteCommentModal';
import useNotificationSettingsModal from 'collaboration-sidebar/hooks/modals/useNotificationSettingsModal';
import useTaskDeleteModal from 'collaboration-sidebar/hooks/modals/useTaskDeleteModal';
import { useConfirmEditRepeatingTaskModal } from 'collaboration-sidebar-common/features/repeatingTasks/hooks/useConfirmEditRepeatingTaskModal';
import { SUPPORTED_HUBS } from 'collaboration-sidebar/constants/supportedHubs';
import useSidebarOpenCloseHandlers from '../hooks/useSidebarOpenCloseHandlers';
import useHasSidebarPermission from '../hooks/useHasSidebarPermission';
import AsyncMessageHandlers from './MessageHandlers/AsyncMessageHandlers';
import Modals from './Modals';
import EmbeddedCommentSetup from './EmbeddedCommentSetup';
import { useCollaborationSidebarState } from '../hooks';
import { trackInteraction } from '../utils/usageTracker';
import useGetPropsFromFAS from '../features/fas/hooks/useGetPropsFromFAS';
import useStableReferences from '../hooks/useStableReferences';
import ErrorCountWrapper from './errors/ErrorCountWrapper';
import { SidebarMessageHandler } from '../features/sidebar/SidebarMessageHandler';
import useCollaborationSidebarDataFetchingClient from '../hooks/useCollaborationSidebarDataFetchingClient';
import { DataFetchingClientProvider } from 'data-fetching-client';
import { Metrics } from '../utils/metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function useSidebarInitMetrics(messageHandlersReady, appName, objectType, withoutSidebar) {
  const initStartTimeRef = useRef(null);
  useEffect(() => {
    if (initStartTimeRef.current === null) {
      initStartTimeRef.current = performance.now();
    }
  }, []);
  useEffect(() => {
    if (messageHandlersReady && initStartTimeRef.current && appName && objectType) {
      const duration = performance.now() - initStartTimeRef.current;
      Metrics.timer('sidebar-init-duration', {
        app_name: appName,
        object_type: objectType,
        has_sidebar: String(!withoutSidebar)
      }).update(duration);
      initStartTimeRef.current = null;
    }
  }, [messageHandlersReady, appName, objectType, withoutSidebar]);
}
function ControlledCollaborationSidebarProvider(_ref) {
  'use no memo';

  var _fasProps2, _fasProps3, _fasProps4;
  let {
      appHub: appHubProp,
      children,
      withoutSidebar: withoutSidebarProp,
      onReady,
      onContentReady,
      onContentError,
      iframeContainerID
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  } = useCollaborationSidebarState();

  /**
   * Keep backwards compatibility by providing default values for FAS props when not ungated or when FAS is available.
   */
  const defaultFasProps = useMemo(() => ({
    appHub: appHubProp && Array.isArray(appHubProp) ? appHubProp : appHubProp && [SUPPORTED_HUBS[appHubProp]],
    defaultSidebarPlacement: rest.defaultSidebarPlacement,
    dragAndDropAreas: rest.dragAndDropAreas,
    enabledFeatures: rest.enabledFeatures,
    hideCommentModeAndToggle: rest.hideCommentModeAndToggle,
    isListingPage: rest.isListingPage,
    isOverlay: rest.isOverlay,
    objectType: undefined,
    scrollablePage: rest.scrollablePage,
    showSidebarHeader: rest.showSidebarHeader,
    withoutSidebar: withoutSidebarProp
  }), [appHubProp, rest.defaultSidebarPlacement, rest.dragAndDropAreas, rest.enabledFeatures, rest.hideCommentModeAndToggle, rest.isListingPage, rest.isOverlay, rest.scrollablePage, rest.showSidebarHeader, withoutSidebarProp]);
  let fasProps = defaultFasProps;

  /**
   * Read from Framework App Settings.
   * If not available, fallback to provided props.
   */
  const {
    fasProps: tempProps,
    loading
  } = useGetPropsFromFAS({
    appName: rest.appName,
    enabledFeatures: rest.enabledFeatures
  });
  if (tempProps) {
    fasProps = tempProps;
  }
  const didEmitOnReady = useRef(false);
  const hasShownNoPermissionWarning = useRef(false);
  const pageContentRef = useRef(null);
  const sendMessageRef = useRef(undefined);
  const onMessageRef = useRef(() => {});
  const [messageHandlersReady, setMessageHandlersReady] = useState(false);

  /**
   * Tracks all usages of collaboration-sidebar by app name and object type
   */
  useEffect(() => {
    var _fasProps;
    trackInteraction({
      action: 'render collaboration-sidebar',
      applicationName: rest.appName,
      objectType: (_fasProps = fasProps) === null || _fasProps === void 0 ? void 0 : _fasProps.objectType
    });
  }, [(_fasProps2 = fasProps) === null || _fasProps2 === void 0 ? void 0 : _fasProps2.objectType, rest.appName]);
  useSidebarInitMetrics(messageHandlersReady, rest.appName, (_fasProps3 = fasProps) === null || _fasProps3 === void 0 ? void 0 : _fasProps3.objectType, fasProps.withoutSidebar);
  useEffect(() => {
    if (messageHandlersReady && onReady && !didEmitOnReady.current) {
      didEmitOnReady.current = true;
      onReady();
    }
  }, [messageHandlersReady, onReady]);
  const {
    objectId,
    onCommentsUpdated
  } = rest;
  const expandableSidebarRef = useRef(null);
  const iframeHandlerProps = useSidebarIframeHandlers({
    enabledFeatures: fasProps.enabledFeatures,
    isOpen: collaborationSidebarState.isOpen,
    onContentError,
    onContentReady,
    sendMessageRef,
    withoutSidebar: fasProps.withoutSidebar,
    appName: rest.appName,
    objectType: (_fasProps4 = fasProps) === null || _fasProps4 === void 0 ? void 0 : _fasProps4.objectType
  });
  const hasSidebarPermission = useHasSidebarPermission({
    appHub: fasProps.appHub,
    enabledFeatures: fasProps.enabledFeatures
  });
  useEffect(() => {
    if (!hasShownNoPermissionWarning.current && !hasSidebarPermission && getIsCommentingUrl() && !loading) {
      showWarningNotification({
        title: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingTitle'),
        message: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingBody'),
        sticky: true
      });
      hasShownNoPermissionWarning.current = true;
    }
  }, [hasSidebarPermission, loading]);
  const {
    hasUnreadComments,
    unreadCommentsCount,
    markCommentAsRead,
    refetchComments
  } = useHasUnreadComments(loading ? undefined : fasProps.objectType, objectId);
  const {
    isCommentModeActive
  } = collaborationSidebarState;
  const {
    handleClose
  } = useSidebarOpenCloseHandlers({
    onUpdateCollaborationSidebarState
  });
  const {
    hideEmbeddedComments,
    setHideEmbeddedComments,
    setModuleReply,
    removeModuleReply,
    incrementReplyCountBy,
    startCommentMode,
    cancelCommentMode
  } = useEmbeddedComments({
    moduleId: null,
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  });
  const _useConfirmDeleteComm = useConfirmDeleteCommentModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleConfirmDeleteCommentModalMessage,
      show: showDeleteCommentModal
    } = _useConfirmDeleteComm,
    confirmDeleteCommentModalProps = _objectWithoutPropertiesLoose(_useConfirmDeleteComm, _excluded2);
  const _useNotificationSetti = useNotificationSettingsModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleNotificationSettingsModalMessage,
      show: showNotificationSettingsModal
    } = _useNotificationSetti,
    notificationSettingsModalProps = _objectWithoutPropertiesLoose(_useNotificationSetti, _excluded3);
  const _useTaskDeleteModal = useTaskDeleteModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleTaskUpdatePanelMessage,
      show: showTaskDeleteModal
    } = _useTaskDeleteModal,
    taskDeleteModalProps = _objectWithoutPropertiesLoose(_useTaskDeleteModal, _excluded4);
  const _useConfirmEditRepeat = useConfirmEditRepeatingTaskModal(),
    {
      show: showConfirmEditRepeatingTaskModal
    } = _useConfirmEditRepeat,
    confirmEditRepeatingTaskModalProps = _objectWithoutPropertiesLoose(_useConfirmEditRepeat, _excluded5);
  const [isPageContentRefSet, setIsPageContentRefSet] = useState(false);
  const collaborationSidebarProviderProps = Object.assign({}, rest, {
    pageContentRef,
    isPageContentRefSet,
    setIsPageContentRefSet,
    hasSidebarPermission,
    expandableSidebarRef,
    hasUnreadComments,
    unreadCommentsCount,
    isModalOpen: showDeleteCommentModal || showTaskDeleteModal || showConfirmEditRepeatingTaskModal,
    iframe: iframeHandlerProps,
    onMessageRef,
    messageHandlersReady,
    startCommentMode,
    cancelCommentMode
  }, fasProps);
  return (
    /*#__PURE__*/
    /**
     * Creating a layer here makes sure that if the collaboration sidebar is
     * rendered in an iframe the copilot it uses will only be initialized for
     * the iframe context it's rendered in
     */
    _jsxs(CopilotProvider, {
      createLayer: true,
      children: [/*#__PURE__*/_jsx(AsyncMessageHandlers, {
        collaborationSidebarState: collaborationSidebarState,
        onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState,
        handleTaskUpdatePanelMessage: handleTaskUpdatePanelMessage,
        handleConfirmDeleteCommentModalMessage: handleConfirmDeleteCommentModalMessage,
        handleNotificationSettingsModalMessage: handleNotificationSettingsModalMessage,
        markCommentAsRead: markCommentAsRead,
        handleClose: handleClose,
        refetchComments: refetchComments,
        onCommentsUpdated: onCommentsUpdated,
        preLoadIframe: iframeHandlerProps.preLoadIframe,
        setHideEmbeddedComments: setHideEmbeddedComments,
        hideEmbeddedComments: hideEmbeddedComments,
        sendMessageRef: sendMessageRef,
        setModuleReply: setModuleReply,
        incrementReplyCountBy: incrementReplyCountBy,
        removeModuleReply: removeModuleReply,
        onMessageRef: onMessageRef,
        setMessageHandlersReady: setMessageHandlersReady
      }), /*#__PURE__*/_jsx(CollaborationSidebarPropsContext.Provider, {
        value: collaborationSidebarProviderProps,
        children: /*#__PURE__*/_jsxs(UserSettingsProvider, {
          objectId: objectId,
          children: [/*#__PURE__*/_jsx(SidebarMessageHandler, {}), /*#__PURE__*/_jsx(EmbeddedCommentSetup, {}), /*#__PURE__*/_jsx(Modals, {
            confirmDeleteCommentModalProps: confirmDeleteCommentModalProps,
            taskDeleteModalProps: taskDeleteModalProps,
            confirmEditRepeatingTaskModalProps: confirmEditRepeatingTaskModalProps,
            notificationSettingsModalProps: notificationSettingsModalProps,
            showDeleteCommentModal: showDeleteCommentModal,
            showNotificationSettingsModal: showNotificationSettingsModal,
            showTaskDeleteModal: showTaskDeleteModal,
            showConfirmEditRepeatingTaskModal: showConfirmEditRepeatingTaskModal,
            withoutSidebar: !!fasProps.withoutSidebar,
            isReady: messageHandlersReady,
            IFrameError: iframeHandlerProps.IFrameError,
            iframeContainerID: iframeContainerID
          }), children, isCommentModeActive && /*#__PURE__*/_jsx(CommentModeIndicator, {
            onCancel: () => {
              onUpdateCollaborationSidebarState({
                [IS_COMMENT_MODE_ACTIVE]: false
              });
              trackInteraction({
                action: 'close comment mode'
              });
            }
          })]
        })
      })]
    })
  );
}
const STABLE_REFERENCE_KEYS = ['enabledFeatures', 'appHub'];
function CollaborationSidebarProviderWithState(props) {
  "use memo";

  const $ = _c(14);
  const {
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  } = props;
  let t0;
  if ($[0] !== props) {
    t0 = {
      object: props,
      keys: STABLE_REFERENCE_KEYS
    };
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const stableProps = useStableReferences(t0);
  let t1;
  if ($[2] !== collaborationSidebarState || $[3] !== onUpdateCollaborationSidebarState || $[4] !== stableProps) {
    t1 = {
      collaborationSidebarState,
      onUpdateCollaborationSidebarState,
      collaborationSidebarProps: stableProps
    };
    $[2] = collaborationSidebarState;
    $[3] = onUpdateCollaborationSidebarState;
    $[4] = stableProps;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const collaborationSidebarStateWithPersistedProps = usePersistPropsToCollaborationSidebarState(t1);
  let t2;
  if ($[6] !== collaborationSidebarStateWithPersistedProps || $[7] !== onUpdateCollaborationSidebarState) {
    t2 = {
      collaborationSidebarState: collaborationSidebarStateWithPersistedProps,
      onUpdateCollaborationSidebarState
    };
    $[6] = collaborationSidebarStateWithPersistedProps;
    $[7] = onUpdateCollaborationSidebarState;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const contextValue = t2;
  let t3;
  if ($[9] !== stableProps) {
    t3 = /*#__PURE__*/_jsx(ControlledCollaborationSidebarProvider, Object.assign({}, stableProps));
    $[9] = stableProps;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== contextValue || $[12] !== t3) {
    t4 = /*#__PURE__*/_jsx(CollaborationSidebarStateContext.Provider, {
      value: contextValue,
      children: t3
    });
    $[11] = contextValue;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export default function CollaborationSidebarProviderWithErrorCount(props) {
  "use memo";

  const $ = _c(11);
  const dataFetchingClient = useCollaborationSidebarDataFetchingClient();
  let t0;
  if ($[0] !== props.children) {
    t0 = () => props.children;
    $[0] = props.children;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = /*#__PURE__*/_jsx(CollaborationSidebarProviderWithState, Object.assign({}, props));
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== dataFetchingClient || $[5] !== t1) {
    t2 = /*#__PURE__*/_jsx(DataFetchingClientProvider, {
      client: dataFetchingClient,
      children: t1
    });
    $[4] = dataFetchingClient;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props.appName || $[8] !== t0 || $[9] !== t2) {
    t3 = /*#__PURE__*/_jsx(ErrorCountWrapper, {
      appName: props.appName,
      errorBoundaryFingerprint: "CollaborationSidebarProvider",
      threshold: 2000,
      renderFallback: t0,
      children: t2
    });
    $[7] = props.appName;
    $[8] = t0;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}